/* Lightweight i18n for Clinical & Surgical Review.
 * Translates UI strings, menus and CMS page content.
 * Article content (titles, abstracts, manuscript body) is always English.
 */
import React, { createContext, useContext, useMemo, useState, useEffect } from "react";

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export const LOCALES = ["en", "es"];

export const DICT = {
  en: {
    // Nav
    "nav.home": "Home",
    "nav.current_issue": "Current Issue",
    "nav.articles": "Articles",
    "nav.submit": "Submit",
    "nav.authors": "Authors",
    "nav.editorial_board": "Editorial Board",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.admin": "Admin Login",
    "nav.search": "Search",
    // Header
    "header.search_placeholder": "Search articles by title, keyword, author...",
    "header.search_submit": "Search",
    // Home
    "home.published_articles": "Published Articles",
    "home.published_subtitle": "Recent peer-reviewed articles published in Clinical & Surgical Review.",
    "home.filter_title": "Filter Articles",
    "home.filter_search_placeholder": "Search by title, keyword, author...",
    "home.filter_all_types": "All article types",
    "home.filter_all_specialties": "All specialties",
    "home.filter_all_years": "All years",
    "home.filter_vol_short": "Vol",
    "home.filter_iss_short": "Iss",
    "home.view_all": "View All Articles",
    "home.empty_carousel": "No articles published yet. The journal is preparing its inaugural issue.",
    "home.empty_list": "No published articles match the current filters.",
    "home.loading": "Loading…",
    // Article row
    "article.view_pdf": "View PDF",
    "article.details": "Details",
    "article.visual_abstract": "Visual Abstract",
    // Article detail
    "article.abstract": "Abstract",
    "article.keywords": "Keywords",
    "article.references": "References",
    "article.how_to_cite": "How to cite this article",
    "article.download_pdf": "Download PDF",
    "article.pdf_unavailable": "Official PDF not yet available",
    "article.received": "Received",
    "article.accepted": "Accepted",
    "article.published": "Published",
    "article.doi": "DOI",
    "article.volume_issue": "Volume / Issue",
    "article.pages": "Pages",
    "article.corresponding_author": "Corresponding Author",
    "article.related": "Related Articles",
    "article.license": "License:",
    // Metrics & Share
    "metrics.title": "Article Metrics",
    "metrics.total_views": "Total Views",
    "metrics.html_views": "HTML Views",
    "metrics.pdf_downloads": "PDF Downloads",
    "metrics.shares": "Shares",
    "metrics.citations": "Citations",
    "metrics.last_updated": "Last updated",
    "share.button": "Share",
    "share.whatsapp": "WhatsApp",
    "share.facebook": "Facebook",
    "share.twitter": "X / Twitter",
    "share.linkedin": "LinkedIn",
    "share.copy": "Copy link",
    "share.copied": "Link copied to clipboard",
    "share.text": "Check out this new publication in Clinical & Surgical Review",
    "submit.field.author_photo": "Author photo (Author 1)",
    "submit.author_photo_hint": "This photo appears as the circular avatar next to the article title in listings.",
    "authors.page_subtitle": "Guidelines for manuscript preparation and submission.",
    "home.hero_subtitle": "Journal of Clinical, Surgical and Hospital-Based Research",
    // Articles page
    "articles.title": "All Articles",
    "articles.subtitle": "Browse all published articles in Clinical & Surgical Review.",
    // Current Issue
    "current_issue.title": "Current Issue",
    "current_issue.empty_subtitle": "The editorial team is preparing the next issue.",
    "current_issue.empty": "No current issue published yet.",
    "current_issue.no_articles": "No articles in this issue yet.",
    // Editorial board
    "board.title": "Editorial Board",
    "board.subtitle": "The editorial team behind Clinical & Surgical Review.",
    "board.empty": "Editorial board members will be announced soon.",
    // Submit
    "submit.title": "Submit Manuscript",
    "submit.subtitle": "Submit your manuscript for peer review. No submission fee.",
    "submit.s1": "1. Corresponding Author Information",
    "submit.s2": "2. Manuscript Information",
    "submit.s3": "3. Authors",
    "submit.s4": "4. Files",
    "submit.s5": "5. Declarations",
    "submit.button": "Submit Manuscript",
    "submit.button_submitting": "Submitting…",
    "submit.need_declarations": "Please confirm all declarations to submit.",
    "submit.confirmation_title": "Submission received",
    "submit.confirmation_body": "Thank you for submitting your manuscript to Clinical & Surgical Review. A confirmation email has been queued for the corresponding author.",
    "submit.your_id": "Your Submission ID",
    "submit.apc_note": "There is no fee for submission or initial editorial review. If your manuscript is accepted, an article processing charge of {amount} USD will apply.",
    "submit.add_author": "Add another author",
    "submit.corresponding_checkbox": "Corresponding author",
    "submit.field.first_name": "First Name",
    "submit.field.middle_name": "Middle Name",
    "submit.field.last_name": "Last Name",
    "submit.field.second_last_name": "Second Last Name",
    "submit.field.email": "Email",
    "submit.field.phone": "Phone",
    "submit.field.orcid": "ORCID",
    "submit.field.affiliation": "Affiliation",
    "submit.field.department": "Department",
    "submit.field.institution": "Institution",
    "submit.field.city": "City",
    "submit.field.country": "Country",
    "submit.field.title": "Article Title",
    "submit.field.short_title": "Short Title",
    "submit.field.article_type": "Article Type",
    "submit.field.specialty": "Main Specialty",
    "submit.field.abstract": "Abstract",
    "submit.field.keywords": "Keywords (comma separated)",
    "submit.field.academic_degree": "Academic Degree",
    "submit.field.author_order": "Author Order",
    "submit.field.manuscript": "Main Manuscript File (Word preferred)",
    "submit.field.cover_letter": "Cover Letter",
    "submit.field.ethics": "Ethics Committee Approval",
    "submit.field.consent": "Informed Consent",
    "submit.field.figures": "Figures",
    "submit.field.tables": "Tables",
    "submit.field.supplementary": "Supplementary Material",
    "submit.decl.original": "The manuscript is original.",
    "submit.decl.not_published_elsewhere": "The manuscript has not been published elsewhere.",
    "submit.decl.all_authors_approve": "All authors approve the submission.",
    "submit.decl.conflicts_disclosed": "Conflicts of interest have been disclosed.",
    "submit.decl.funding_disclosed": "Funding information has been disclosed.",
    "submit.decl.ethics_approved": "Ethics approval was obtained when applicable.",
    "submit.decl.consent_obtained": "Informed consent was obtained when applicable.",
    "submit.decl.no_fee_understood": "I understand that there is no fee for submission or initial editorial review.",
    "submit.decl.apc_understood": "I understand that, if the manuscript is accepted for publication, an article processing charge of {amount} USD will apply.",
    "submit.decl.privacy_notice": "I acknowledge that I have read the Privacy Notice and understand how my personal data will be processed for editorial and publication purposes.",
    "submit.choose": "Choose…",
    // Contact
    "contact.title": "Contact",
    "contact.subtitle": "Get in touch with the editorial office.",
    "contact.email": "Email",
    "contact.institution": "Institution",
    "contact.location": "Location",
    "contact.sent_title": "Message received",
    "contact.sent_body": "Thank you. The editorial office will reply soon.",
    "contact.field.name": "Full Name",
    "contact.field.email": "Email",
    "contact.field.subject": "Subject",
    "contact.field.message": "Message",
    "contact.button": "Send Message",
    "contact.button_sending": "Sending…",
    // Footer
    "footer.tagline": "A peer-reviewed digital medical journal dedicated to publishing clinical, surgical and hospital-based research.",
    "footer.for_authors": "For Authors",
    "footer.about": "About",
    "footer.issn": "ISSN (Electronic)",
    "footer.doi_prefix": "DOI Prefix",
    "footer.contact": "Contact",
    "footer.website": "Website",
    "footer.instructions": "Instructions for Authors",
    "footer.submit": "Submit Manuscript",
    "footer.peer_review": "Peer Review Process",
    "footer.ethics": "Publication Ethics",
    "footer.apc": "Article Processing Charge",
    "footer.about_journal": "About the Journal",
    "footer.aims_scope": "Aims & Scope",
    "footer.editorial_board": "Editorial Board",
    "footer.contact_page": "Contact",
    "footer.privacy": "Privacy Policy",
    "footer.legal_notice": "Legal Notice",
    "legal.last_modified": "Last modified",
    "footer.rights": "All rights reserved.",
    "footer.subline": "Clin Surg Rev · Hospital-based research · Peer-reviewed",
    // Admin login
    "login.title": "Editorial Dashboard",
    "login.heading": "Secure Sign In",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign In",
    "login.signing_in": "Signing in…",
    // Misc
    "common.loading": "Loading…",
    "common.required": "*",
    "content.coming_soon": "Content coming soon.",
  },
  es: {
    "nav.home": "Inicio",
    "nav.current_issue": "Número Actual",
    "nav.articles": "Artículos",
    "nav.submit": "Enviar Manuscrito",
    "nav.authors": "Autores",
    "nav.editorial_board": "Comité Editorial",
    "nav.about": "Acerca de",
    "nav.contact": "Contacto",
    "nav.admin": "Acceso Admin",
    "nav.search": "Buscar",
    "header.search_placeholder": "Buscar artículos por título, palabra clave, autor...",
    "header.search_submit": "Buscar",
    "home.published_articles": "Artículos Publicados",
    "home.published_subtitle": "Artículos recientes revisados por pares publicados en Clinical & Surgical Review.",
    "home.filter_title": "Filtrar Artículos",
    "home.filter_search_placeholder": "Buscar por título, palabra clave, autor...",
    "home.filter_all_types": "Todos los tipos de artículo",
    "home.filter_all_specialties": "Todas las especialidades",
    "home.filter_all_years": "Todos los años",
    "home.filter_vol_short": "Vol",
    "home.filter_iss_short": "Nº",
    "home.view_all": "Ver Todos los Artículos",
    "home.empty_carousel": "Aún no hay artículos publicados. La revista está preparando su número inaugural.",
    "home.empty_list": "Ningún artículo publicado coincide con los filtros actuales.",
    "home.loading": "Cargando…",
    "article.view_pdf": "Ver PDF",
    "article.details": "Detalles",
    "article.visual_abstract": "Resumen Visual",
    "article.abstract": "Resumen",
    "article.keywords": "Palabras clave",
    "article.references": "Referencias",
    "article.how_to_cite": "Cómo citar este artículo",
    "article.download_pdf": "Descargar PDF",
    "article.pdf_unavailable": "PDF oficial aún no disponible",
    "article.received": "Recibido",
    "article.accepted": "Aceptado",
    "article.published": "Publicado",
    "article.doi": "DOI",
    "article.volume_issue": "Volumen / Número",
    "article.pages": "Páginas",
    "article.corresponding_author": "Autor de Correspondencia",
    "article.related": "Artículos Relacionados",
    "article.license": "Licencia:",
    "metrics.title": "Métricas del Artículo",
    "metrics.total_views": "Visualizaciones Totales",
    "metrics.html_views": "Lecturas Web (HTML)",
    "metrics.pdf_downloads": "Descargas PDF",
    "metrics.shares": "Veces Compartido",
    "metrics.citations": "Citas",
    "metrics.last_updated": "Última actualización",
    "share.button": "Compartir",
    "share.whatsapp": "WhatsApp",
    "share.facebook": "Facebook",
    "share.twitter": "X / Twitter",
    "share.linkedin": "LinkedIn",
    "share.copy": "Copiar enlace",
    "share.copied": "Enlace copiado al portapapeles",
    "share.text": "Mira esta nueva publicación en Clinical & Surgical Review",
    "submit.field.author_photo": "Foto del autor (Autor 1)",
    "submit.author_photo_hint": "Esta foto aparecerá como el avatar circular junto al título del artículo en los listados.",
    "authors.page_subtitle": "Guía para la preparación y el envío de manuscritos.",
    "home.hero_subtitle": "Revista de Investigación Clínica, Quirúrgica y Hospitalaria",
    "articles.title": "Todos los Artículos",
    "articles.subtitle": "Explore todos los artículos publicados en Clinical & Surgical Review.",
    "current_issue.title": "Número Actual",
    "current_issue.empty_subtitle": "El equipo editorial está preparando el próximo número.",
    "current_issue.empty": "Aún no hay número actual publicado.",
    "current_issue.no_articles": "Aún no hay artículos en este número.",
    "board.title": "Comité Editorial",
    "board.subtitle": "El equipo editorial detrás de Clinical & Surgical Review.",
    "board.empty": "Próximamente se anunciarán los miembros del comité editorial.",
    "submit.title": "Enviar Manuscrito",
    "submit.subtitle": "Envíe su manuscrito para revisión por pares. Sin costo de envío.",
    "submit.s1": "1. Información del Autor de Correspondencia",
    "submit.s2": "2. Información del Manuscrito",
    "submit.s3": "3. Autores",
    "submit.s4": "4. Archivos",
    "submit.s5": "5. Declaraciones",
    "submit.button": "Enviar Manuscrito",
    "submit.button_submitting": "Enviando…",
    "submit.need_declarations": "Por favor confirme todas las declaraciones para enviar.",
    "submit.confirmation_title": "Envío recibido",
    "submit.confirmation_body": "Gracias por enviar su manuscrito a Clinical & Surgical Review. Se ha encolado un correo de confirmación para el autor de correspondencia.",
    "submit.your_id": "Su ID de Envío",
    "submit.apc_note": "No hay costo de envío ni de revisión editorial inicial. Si su manuscrito es aceptado, se aplicará un cargo de procesamiento de artículo de {amount} USD.",
    "submit.add_author": "Añadir otro autor",
    "submit.corresponding_checkbox": "Autor de correspondencia",
    "submit.field.first_name": "Nombre",
    "submit.field.middle_name": "Segundo Nombre",
    "submit.field.last_name": "Apellido",
    "submit.field.second_last_name": "Segundo Apellido",
    "submit.field.email": "Correo electrónico",
    "submit.field.phone": "Teléfono",
    "submit.field.orcid": "ORCID",
    "submit.field.affiliation": "Afiliación",
    "submit.field.department": "Departamento",
    "submit.field.institution": "Institución",
    "submit.field.city": "Ciudad",
    "submit.field.country": "País",
    "submit.field.title": "Título del Artículo",
    "submit.field.short_title": "Título Corto",
    "submit.field.article_type": "Tipo de Artículo",
    "submit.field.specialty": "Especialidad Principal",
    "submit.field.abstract": "Resumen",
    "submit.field.keywords": "Palabras clave (separadas por coma)",
    "submit.field.academic_degree": "Grado Académico",
    "submit.field.author_order": "Orden del Autor",
    "submit.field.manuscript": "Archivo Principal del Manuscrito (preferiblemente Word)",
    "submit.field.cover_letter": "Carta de Presentación",
    "submit.field.ethics": "Aprobación del Comité de Ética",
    "submit.field.consent": "Consentimiento Informado",
    "submit.field.figures": "Figuras",
    "submit.field.tables": "Tablas",
    "submit.field.supplementary": "Material Suplementario",
    "submit.decl.original": "El manuscrito es original.",
    "submit.decl.not_published_elsewhere": "El manuscrito no ha sido publicado en otro lugar.",
    "submit.decl.all_authors_approve": "Todos los autores aprueban el envío.",
    "submit.decl.conflicts_disclosed": "Los conflictos de interés han sido declarados.",
    "submit.decl.funding_disclosed": "La información de financiación ha sido declarada.",
    "submit.decl.ethics_approved": "Se obtuvo aprobación ética cuando fue aplicable.",
    "submit.decl.consent_obtained": "Se obtuvo consentimiento informado cuando fue aplicable.",
    "submit.decl.no_fee_understood": "Entiendo que no hay costo de envío ni de revisión editorial inicial.",
    "submit.decl.apc_understood": "Entiendo que, si el manuscrito es aceptado para publicación, se aplicará un cargo de procesamiento de artículo de {amount} USD.",
    "submit.decl.privacy_notice": "Reconozco que he leído el Aviso de Privacidad y entiendo cómo se procesarán mis datos personales con fines editoriales y de publicación.",
    "submit.choose": "Elegir…",
    "contact.title": "Contacto",
    "contact.subtitle": "Póngase en contacto con la oficina editorial.",
    "contact.email": "Correo electrónico",
    "contact.institution": "Institución",
    "contact.location": "Ubicación",
    "contact.sent_title": "Mensaje recibido",
    "contact.sent_body": "Gracias. La oficina editorial responderá pronto.",
    "contact.field.name": "Nombre Completo",
    "contact.field.email": "Correo electrónico",
    "contact.field.subject": "Asunto",
    "contact.field.message": "Mensaje",
    "contact.button": "Enviar Mensaje",
    "contact.button_sending": "Enviando…",
    "footer.tagline": "Revista médica digital revisada por pares dedicada a publicar investigación clínica, quirúrgica y hospitalaria.",
    "footer.for_authors": "Para Autores",
    "footer.about": "Acerca de",
    "footer.issn": "ISSN (Electrónico)",
    "footer.doi_prefix": "Prefijo DOI",
    "footer.contact": "Contacto",
    "footer.website": "Sitio web",
    "footer.instructions": "Instrucciones para Autores",
    "footer.submit": "Enviar Manuscrito",
    "footer.peer_review": "Proceso de Revisión por Pares",
    "footer.ethics": "Ética de Publicación",
    "footer.apc": "Cargo de Procesamiento de Artículo",
    "footer.about_journal": "Acerca de la Revista",
    "footer.aims_scope": "Objetivos y Alcance",
    "footer.editorial_board": "Comité Editorial",
    "footer.contact_page": "Contacto",
    "footer.privacy": "Política de Privacidad",
    "footer.legal_notice": "Aviso Legal",
    "legal.last_modified": "Última modificación",
    "footer.rights": "Todos los derechos reservados.",
    "footer.subline": "Clin Surg Rev · Investigación hospitalaria · Revisada por pares",
    "login.title": "Panel Editorial",
    "login.heading": "Acceso Seguro",
    "login.email": "Correo electrónico",
    "login.password": "Contraseña",
    "login.submit": "Iniciar Sesión",
    "login.signing_in": "Iniciando sesión…",
    "common.loading": "Cargando…",
    "common.required": "*",
    "content.coming_soon": "Contenido próximamente.",
  },
};

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem("csr_lang");
    return LOCALES.includes(saved) ? saved : "en";
  });

  useEffect(() => {
    localStorage.setItem("csr_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (l) => {
    if (LOCALES.includes(l)) setLangState(l);
  };

  const t = useMemo(() => {
    const dict = DICT[lang] || DICT.en;
    return (key, fallback) => dict[key] ?? DICT.en[key] ?? fallback ?? key;
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
